import { useState } from 'react';
import { makeStyles, Typography } from '@ellucian/react-design-system/core';
import { useIntl } from 'react-intl';
import StepProgress, { Step, StepLabel } from '@ellucian/react-design-system/core/StepProgress';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@ellucian/ds-icons/lib';
import {
    spacing30,
    spacing10,
    fontWeightBold,
    colorBrandNeutral200,
    colorTextNeutral600,
    colorGlobalBorderDefault,
    borderRadiusSmall,
    durationMedium} from '@ellucian/react-design-system/core/styles/tokens';

const useStyles = makeStyles({
    name: 'MessageHistory'
})(() => ({
    container: {
        marginTop: spacing30,
        backgroundColor: colorBrandNeutral200,
        borderRadius: borderRadiusSmall,
        border: `1px solid ${colorGlobalBorderDefault}`,
        padding: spacing30 },
    header: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: 0,
        background: 'none',
        border: 'none',
        width: '100%',
        textAlign: 'left' },
    headerText: {
        fontWeight: fontWeightBold,
        marginRight: spacing10 },
    expandIcon: {
        transition: `transform ${durationMedium} cubic-bezier(0.4, 0, 0.2, 1)` },
    expandIconRotated: {
        transform: 'rotate(180deg)' },
    collapseContent: {
        maxHeight: 0,
        overflow: 'hidden',
        transition: `max-height ${durationMedium} cubic-bezier(0.4, 0, 0.2, 1)` },
    collapseContentExpanded: {
        maxHeight: '2000px' },
    stepProgressContainer: {
        marginTop: spacing30,
        '& .MuiStepLabel-label': {
            marginTop: spacing10 },
        '& .MuiStepIcon-text': {
            display: 'none' },
        '& .MuiStepLabel-iconContainer': {
            paddingRight: spacing30 } },
    senderName: {
        fontWeight: fontWeightBold },
    timestamp: {
        color: colorTextNeutral600 } }));

/**
 * Format date for message display
 * @param {string} dateString - ISO date string
 * @param {string} locale - Locale code
 * @returns {string} Formatted date and time
 */
const formatMessageDate = (dateString, locale = 'en-US') => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true });
};

const MessageHistory = ({ comments = [], locale = 'en-US' }) => {
    const { classes } = useStyles();
    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);

    if (!Array.isArray(comments) || comments.length === 0) {
        return null;
    }

    // Sort comments by date descending (latest first)
    const sortedComments = [...comments].sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    /**
     * Determine if the comment is from a reviewer or applicant
     * Reviewers have reviewerId/reviewerName, applicants have isApplicantComment flag
     */
    const isReviewerComment = (comment) => {
        // Explicitly check for applicant flag first
        if (comment.isApplicantComment === true) {
            return false;
        }
        return Boolean(comment.reviewerId || comment.reviewerName);
    };

    /**
     * Get display name for the comment author
     */
    const getAuthorName = (comment) => {
        if (isReviewerComment(comment)) {
            return comment.reviewerName || intl.formatMessage({ id: 'messageHistoryReviewer' });
        }
        // Use applicantName if available, show "(You)" suffix for applicant's own messages
        const applicantName = comment.applicantName || intl.formatMessage({ id: 'messageHistoryYou' });
        if (comment.applicantName) {
            return `${applicantName} (${intl.formatMessage({ id: 'messageHistoryYou' })})`;
        }
        return applicantName;
    };

    return (
        <div className={classes.container} data-testid="MessageHistory">
            <button
                type="button"
                className={classes.header}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-controls="message-history-content"
                aria-label={intl.formatMessage({ id: 'messageHistoryTitle' })}
            >
                <Typography id="message-history-header" className={classes.headerText}>
                    <FormattedMessage id="messageHistoryTitle" />
                </Typography>
                <Icon
                    name="chevron-down"
                    className={`${classes.expandIcon} ${isExpanded ? classes.expandIconRotated : ''}`}
                    aria-hidden="true"
                />
            </button>
            <div 
                id="message-history-content"
                className={`${classes.collapseContent} ${isExpanded ? classes.collapseContentExpanded : ''}`}
                role="region"
                aria-labelledby="message-history-header"
                hidden={!isExpanded}
            >
                <div className={classes.stepProgressContainer}>
                    <StepProgress 
                        activeStep={sortedComments.length - 1}
                        orientation="vertical"
                    >
                        {sortedComments.map((comment, index) => (
                            <Step key={comment.createdAt || index} completed>
                                <StepLabel>
                                    <Typography variant="body2" className={classes.senderName}>
                                        {getAuthorName(comment)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {comment.comment}
                                    </Typography>
                                    <Typography variant="body3" className={classes.timestamp}>
                                        {formatMessageDate(comment.createdAt, locale)}
                                    </Typography>
                                </StepLabel>
                            </Step>
                        ))}
                    </StepProgress>
                </div>
            </div>
        </div>
    );
};

export default MessageHistory;
